
import { defineComponent, reactive, toRefs, computed, watch } from 'vue';
import * as FarmPoolPageComponents from '@/components/contextual/pages/farm';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import useNumbers from '@/composables/useNumbers';
import { usePool, isStableLike, isStablePhantom } from '@/composables/usePool';
import useFarmPoolQuery from '@/composables/queries/useFarmPoolQuery';
import { POOLS } from '@/constants/pools';
import { EXTERNAL_LINKS } from '@/constants/links';
import useWeb3 from '@/services/web3/useWeb3';
import useTokens from '@/composables/useTokens';
import useApp from '@/composables/useApp';
import { getAddress } from '@ethersproject/address';
import useAlerts, { AlertPriority, AlertType } from '@/composables/useAlerts';
import {
  DecoratedPoolWithShares,
  PoolToken
} from '@/services/balancer/subgraph/types';
import FarmNavCard from '@/components/cards/FarmNavCard/FarmNavCard.vue';
import FarmAction from './FarmAction.vue';
import useUserClaimsFlakeQuery from '@/composables/queries/useUserClaimsFlakeQuery';
import useUserFarmPoolsQuery from '@/composables/queries/useUserFarmPoolsQuery';

interface PoolPageData {
  farmId: string;
}

export default defineComponent({
  components: {
    ...FarmPoolPageComponents,
    FarmNavCard,
    FarmAction
  },

  setup() {
    /**
     * COMPOSABLES
     */
    const { appLoading } = useApp();
    const { t } = useI18n();
    const route = useRoute();
    const { fNum2 } = useNumbers();
    const { isWalletReady } = useWeb3();
    const { prices } = useTokens();
    const { blockNumber } = useWeb3();
    const { addAlert, removeAlert } = useAlerts();
    const { balancerTokenListTokens } = useTokens();
    const farmId = route.params.farmId as string;
    const { data: userPoolsQuery } = useUserFarmPoolsQuery();
    /**
     * QUERIES
     */
    const poolQuery = useFarmPoolQuery(farmId);
    // const poolSnapshotsQuery = usePoolSnapshotsQuery(
    //   route.params.id as string,
    //   30
    // );

    /**
     * STATE
     */
    const data = reactive<PoolPageData>({
      farmId: route.params.farmId as string
    });

    /**
     * COMPUTED
     */
    const pool = computed(() => poolQuery.data.value);
    const userPools = computed(() => {
      if (userPoolsQuery.value && userPoolsQuery.value.pools) {
        let tmp = userPoolsQuery.value.pools.filter(
          i => i.pid === farmId.split('_')[1]
        );
        return tmp.length !== 0 ? tmp[0] : {};
      } else {
        return {};
      }
    });
    const userClaimsFlakeQuery = useUserClaimsFlakeQuery([
      farmId.split('_')[1]
    ]);
    const userClaimFlakeStatus = computed(() =>
      userClaimsFlakeQuery.data.value ? userClaimsFlakeQuery.data?.value : []
    );
    const {
      isStableLikePool,
      isLiquidityBootstrappingPool,
      isStablePhantomPool
    } = usePool(poolQuery.data);

    const noInitLiquidity = computed(
      () =>
        !loadingPool.value &&
        pool.value &&
        Number(pool.value.onchain.totalSupply) === 0
    );

    const communityManagedFees = computed(
      () => pool.value?.owner == POOLS.DelegateOwner
    );
    const feesManagedByGauntlet = computed(
      () =>
        communityManagedFees.value &&
        POOLS.DynamicFees.Gauntlet.includes(data.farmId)
    );
    const feesFixed = computed(() => pool.value?.owner == POOLS.ZeroAddress);
    const swapFeeToolTip = computed(() => {
      if (feesManagedByGauntlet.value) {
        return t('feesManagedByGauntlet');
      } else if (communityManagedFees.value) {
        return t('delegateFeesTooltip');
      } else if (feesFixed.value) {
        return t('fixedFeesTooltip');
      } else {
        return t('ownerFeesTooltip');
      }
    });

    const poolQueryLoading = computed(
      () =>
        poolQuery.isLoading.value ||
        poolQuery.isIdle.value ||
        poolQuery.error.value
    );

    const userClaimsFlakeQueryLoading = computed(
      () =>
        userClaimsFlakeQuery.isLoading.value ||
        userClaimsFlakeQuery.isIdle.value ||
        userClaimsFlakeQuery.error.value
    );

    const loadingPool = computed(
      () =>
        poolQueryLoading.value ||
        !pool.value ||
        userClaimsFlakeQueryLoading.value
    );

    // const snapshots = computed(() => poolSnapshotsQuery.data.value?.snapshots);
    // const historicalPrices = computed(
    //   () => poolSnapshotsQuery.data.value?.prices
    // );
    // const isLoadingSnapshots = computed(
    //   () =>
    //     poolSnapshotsQuery.isLoading.value || poolSnapshotsQuery.isIdle.value
    // );

    const titleTokens = computed(() => {
      if (!pool.value) return [];
      return Object.entries(pool.value.onchain.tokens).sort(
        ([, a]: any[], [, b]: any[]) => b.weight - a.weight
      );
    });

    const poolTypeLabel = computed(() => {
      if (!pool.value) return '';
      const key = POOLS.Factories[pool.value.factory];

      return key ? t(key) : t('unknownPoolType');
    });

    const missingPrices = computed(() => {
      if (pool.value) {
        const tokensWithPrice = Object.keys(prices.value);

        const tokens =
          isStablePhantomPool.value && pool.value.mainTokens
            ? pool.value.mainTokens
            : pool.value.tokenAddresses;

        return !tokens.every(token => tokensWithPrice.includes(token));
      }
      return false;
    });

    const hasCustomToken = computed(() => {
      const knownTokens = Object.keys(balancerTokenListTokens.value);
      return (
        !!pool.value &&
        !isLiquidityBootstrappingPool.value &&
        !isStablePhantomPool.value &&
        pool.value.tokenAddresses.some(
          address => !knownTokens.includes(address)
        )
      );
    });

    /**
     * METHODS
     */
    function onNewTx(): void {
      poolQuery.refetch.value();
    }

    function orderedTokenAddressesFor(pool: DecoratedPoolWithShares) {
      const sortedTokens = orderedPoolTokens(pool);
      return sortedTokens.map(token => getAddress(token.address));
    }

    function orderedPoolTokens(pool: DecoratedPoolWithShares): PoolToken[] {
      if (isStablePhantom(pool.poolType))
        return pool.tokens.filter(token => token.address !== pool.address);
      if (isStableLike(pool.poolType)) return pool.tokens;

      const sortedTokens = pool.tokens.slice();
      sortedTokens.sort((a, b) => parseFloat(b.weight) - parseFloat(a.weight));
      return sortedTokens;
    }

    /**
     * WATCHERS
     */
    watch(blockNumber, () => {
      poolQuery.refetch.value();
      userClaimsFlakeQuery.refetch.value();
    });

    watch(poolQuery.error, () => {
      if (poolQuery.error.value) {
        addAlert({
          id: 'pool-fetch-error',
          label: t('alerts.pool-fetch-error'),
          type: AlertType.ERROR,
          persistent: true,
          action: poolQuery.refetch.value,
          actionLabel: t('alerts.retry-label'),
          priority: AlertPriority.MEDIUM
        });
      } else {
        removeAlert('pool-fetch-error');
      }
    });

    return {
      // data
      ...toRefs(data),
      EXTERNAL_LINKS,
      // computed
      appLoading,
      pool,
      userPools,
      noInitLiquidity,
      farmId,
      poolTypeLabel,
      // poolFeeLabel,
      // historicalPrices,
      // snapshots,
      // isLoadingSnapshots,
      loadingPool,
      titleTokens,
      isWalletReady,
      missingPrices,
      feesManagedByGauntlet,
      swapFeeToolTip,
      isStableLikePool,
      isLiquidityBootstrappingPool,
      isStablePhantomPool,
      hasCustomToken,
      // methods
      fNum2,
      onNewTx,
      orderedTokenAddressesFor,
      userClaimFlakeStatus
    };
  }
});
